import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Paperclip, ArrowsClockwise, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DateField } from '@/components/ui/date-time-field';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CommonRegistrationBlock, FormSection, finalizeNumber } from '@/components/registration';
import { bihinApi, STATUS_META } from './bihinApi';

const EMPTY = {
  name: '', kind: 'equipment', asset_no: '', category: '', department_id: '',
  location: '', location_id: '', status: 'storage', maker: '', model_no: '', acquired_on: '',
  acquisition_price: '', photo_url: '', photo_filename: '', photo_is_pdf: false, description: '',
  next_inspection_date: '', inspection_interval_months: '',
  qty: '', uom: '個', min_qty: '',
  purchase_unit: '', pack_qty: '', pack_price: '',
  maker_pn: '', serial_no: '', reference_url: '', supplier_id: '', supplier_name: '',
  // TECHS 発注に必要な情報（どの台帳でも同じ項目名）
  number_value: null, sku_group_id: '', order_category_id: '', order_category_name: '',
};

// 共通ブロックで出す項目（仕様 2.6 ＋ TECHS 整合）。
const FIELDS_BASE = [
  'master_search', 'name', 'category', 'kind', 'maker', 'notes',
  'number', 'sku_group', 'order_category', 'model_no', 'maker_pn', 'uom',
  'supplier', 'reference_url', 'location', 'department', 'acquired_on', 'price',
];

// ＊ と 要入力 の対象。**保存を止める条件は今までどおり**（下の `save` を参照）。
const REQUIRED_BASE = ['name', 'number', 'category', 'model_no', 'location',
  'order_category', 'uom'];

/**
 * F5 · 備品を登録 / 編集。
 *
 * 2026-06 · 共通ブロック（`CommonRegistrationBlock`）に寄せた。項目・並び・名前は
 * F1〜F6 で同一。備品だけの追加は 種別（貸出可/消耗品）・消耗品の在庫まわり・
 * 点検・写真。**保存を止める条件は今までと同じ**（新しい TECHS 項目は警告だけ）。
 */
export default function BihinItemFormDialog({ open, onOpenChange, item, departments, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const editing = !!item;
  const isConsumable = form.kind === 'consumable';
  // R1 · 備品の番号は 部署/種類（既定は共通 CMN → `CMN/CSM00009`）。
  const DEPT_CODES = ['SNS', 'SOL', 'HTR', 'ENG', 'IT', 'CMN'];
  const deptCode = (() => {
    const row = (departments || []).find((d) => d.id === form.department_id);
    const code = String(row?.code || '').toUpperCase();
    if (code === 'COMMON') return 'CMN';
    return DEPT_CODES.includes(code) ? code : 'CMN';
  })();

  useEffect(() => {
    if (!open) return;
    if (item) {
      setForm({
        ...EMPTY,
        ...Object.fromEntries(Object.entries(item)
          .filter(([k]) => k in EMPTY).map(([k, v]) => [k, v ?? ''])),
        acquired_on: item.purchase_date || '',
        acquisition_price: item.price ?? '',
        description: item.notes || '',
        qty: item.stock_qty ?? '',
        uom: item.unit || '個',
        location: item.location || '',
        location_id: item.location_id || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, item]);

  // 消耗品は CSM、貸出可の備品は EQP（どちらも設備台帳と同じ番号空間）。
  useEffect(() => {
    if (editing || form.number_value?.mode === 'template') return;
    const want = isConsumable ? 'CSM' : 'EQP';
    if (form.number_value?.shared_prefix !== want) {
      setForm((p) => ({ ...p, number_value: { mode: 'shared', shared_prefix: want } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConsumable, editing]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const packQty = Number(form.pack_qty);
  const packLabel = packQty > 0
    ? `1${(form.purchase_unit || 'パック')} = ${packQty}${form.uom || '個'}` : null;
  const packPrice = form.pack_price === '' ? null : Number(form.pack_price);
  const unitPrice = packQty > 0 && packPrice != null && !Number.isNaN(packPrice)
    ? Math.round((packPrice / packQty) * 100) / 100 : null;

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await bihinApi.upload(file);
      setForm((p) => ({ ...p, photo_url: r.photo_url, photo_filename: r.filename, photo_is_pdf: r.is_pdf }));
      toast.success('ファイルを添付しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    // 止める条件は 2026-06 の変更前と同じ（新規登録のときだけ・同じ項目）。
    // 新しく足した TECHS 項目は ＊ と 要入力 で知らせるだけで、保存は止めない。
    const missing = [];
    if (!form.name.trim()) missing.push('名前');
    const willAutoNumber = !!form.number_value?.mode;
    if (!item) {
      if (!willAutoNumber && !(form.asset_no || '').trim()) missing.push('品番 / 資産番号');
      if (!(form.category || '').trim()) missing.push('カテゴリ');
      if (!(form.model_no || '').trim()) missing.push('型式・仕様');
      if (!(form.location_id || form.location || '').trim()) missing.push('設置場所');
      if (isConsumable) {
        const q = form.qty;
        if (q === '' || q === null || q === undefined || Number.isNaN(Number(q)) || Number(q) < 0) {
          missing.push('数量');
        }
        if (!(form.uom || '').trim()) missing.push('単位');
      }
    }
    if (missing.length) {
      toast.error(`必須項目を入力してください: ${missing.map((m) => `「${m}」`).join('、')}`);
      return;
    }
    setBusy(true);
    let finalNo = form.asset_no;
    if (!editing) {
      try {
        finalNo = (await finalizeNumber(form.number_value)) || form.asset_no;
      } catch (e) {
        toast.error(e?.response?.data?.detail || '管理番号の正式発番に失敗しました');
        setBusy(false);
        return;
      }
    }
    const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    const payload = {
      ...form,
      name: form.name.trim(),
      department_id: form.department_id || null,
      dept_code: deptCode,
      asset_no: finalNo || null,
      // 番号を共通採番に任せるときだけサーバーに発番させる
      auto_shared_no: !editing && !finalNo && form.number_value?.mode === 'shared',
      location: form.location || null,
      location_id: form.location_id || null,
      notes: form.description || null,
      purchase_date: form.acquired_on || null,
      inspection_interval_months: num(form.inspection_interval_months),
      stock_qty: isConsumable ? num(form.qty) ?? 0 : null,
      unit: form.uom || null,
      min_qty: isConsumable ? num(form.min_qty) : null,
      purchase_unit: isConsumable ? (form.purchase_unit || null) : null,
      pack_qty: isConsumable ? num(form.pack_qty) : null,
      pack_price: isConsumable ? num(form.pack_price) : null,
      price: num(form.acquisition_price) ?? (isConsumable ? unitPrice : null),
      next_inspection_date: form.next_inspection_date || null,
      maker_pn: form.maker_pn || null,
      serial_no: form.kind === 'equipment' ? (form.serial_no || null) : null,
      reference_url: form.reference_url || null,
      supplier_id: form.supplier_id || null,
      supplier_name: form.supplier_name || null,
      photo_url: form.photo_url || null,
      photo_filename: form.photo_filename || null,
      sku_group_id: form.sku_group_id || form.number_value?.sku_group_code || null,
      order_category_id: form.order_category_id || null,
      order_category_name: form.order_category_name || null,
      sku_template_code: form.number_value?.template_code || null,
    };
    try {
      if (item) await bihinApi.updateItem(item.id, payload);
      else await bihinApi.createItem(payload);
      toast.success(item ? '更新しました' : '登録しました');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const fields = [...FIELDS_BASE,
    ...(isConsumable ? ['qty'] : ['serial_no', 'status'])];
  const requiredKeys = [...REQUIRED_BASE, ...(isConsumable ? ['qty'] : [])];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="bihin-item-form-dialog">
        <DialogHeader>
          <DialogTitle>{item ? '備品を編集' : '備品を登録'}</DialogTitle>
        </DialogHeader>

        <CommonRegistrationBlock
          value={form} onChange={setForm} editing={editing}
          fields={fields} requiredKeys={requiredKeys}
          askAcquisitionType
          register="bihin" appCode="office_equipment" currentNo={item?.asset_no || '—'}
          departments={departments || []} deptCode={deptCode}
          kindOptions={[{ value: 'equipment', label: '備品（貸出可）' },
            { value: 'consumable', label: '消耗品（在庫管理）' }]}
          statusOptions={isConsumable ? []
            : Object.entries(STATUS_META).map(([value, m]) => ({ value, label: m.label }))}
          testPrefix="bihin-form"
          extra={(
            <>
              {isConsumable && (
                <FormSection title="在庫（消耗品）" testId="bihin-form-section-stock">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">最低在庫</Label>
                      <Input type="number" value={form.min_qty} onChange={set('min_qty')}
                        className="h-10 mt-1" placeholder="下回ると警告" data-testid="bihin-form-min-qty" />
                    </div>
                  </div>
                  <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-2.5">
                    <p className="text-[11px] font-semibold text-zinc-600">まとめ売りの単位（任意）</p>
                    <p className="mt-0.5 text-[10px] text-zinc-400">
                      例: 1セット = 30本 のとき「購入単位=セット / 入数=30」。在庫数・価格は在庫単位（{form.uom || '個'}）のままです。
                    </p>
                    <div className="mt-2 grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">購入単位</Label>
                        <Input value={form.purchase_unit} onChange={set('purchase_unit')}
                          className="h-10 mt-1" placeholder="例: セット" data-testid="bihin-form-purchase-unit" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">入数</Label>
                        <Input type="number" min="1" value={form.pack_qty} onChange={set('pack_qty')}
                          className="h-10 mt-1" placeholder="例: 30" data-testid="bihin-form-pack-qty" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">パック価格 (円)</Label>
                        <Input type="number" min="0" value={form.pack_price} onChange={set('pack_price')}
                          className="h-10 mt-1" data-testid="bihin-form-pack-price" />
                      </div>
                    </div>
                    {packLabel && (
                      <p className="mt-2 text-[11px] font-semibold text-teal-700" data-testid="bihin-form-pack-hint">
                        {packLabel}
                        {unitPrice != null && `・単価（1${form.uom || '個'}あたり）¥${unitPrice.toLocaleString()}`}
                      </p>
                    )}
                  </div>
                </FormSection>
              )}

              {!isConsumable && (
                <FormSection title="点検・校正" testId="bihin-form-section-inspection">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">点検間隔（月）</Label>
                      <Input type="number" value={form.inspection_interval_months}
                        onChange={set('inspection_interval_months')} className="h-10 mt-1"
                        placeholder="例: 12" data-testid="bihin-form-inspection-interval" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">次回点検日</Label>
                      <DateField value={form.next_inspection_date}
                        onChange={(v) => set('next_inspection_date')(v)} className="mt-1"
                        inputClassName="h-10" testId="bihin-form-inspection-date" />
                    </div>
                  </div>
                </FormSection>
              )}

              <FormSection title="写真・添付" testId="bihin-form-section-photo">
                <Label className="text-xs font-semibold text-zinc-600">写真 / 添付 (画像・PDF, 25MBまで)</Label>
                <div className="mt-1 flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden"
                    onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="bihin-form-file-input" />
                  <Button type="button" variant="outline" className="h-9" disabled={uploading}
                    onClick={() => fileRef.current?.click()} data-testid="bihin-form-upload-btn">
                    {uploading ? <ArrowsClockwise className="animate-spin" size={14} /> : <Paperclip size={14} />}
                    <span className="ml-1">{uploading ? 'アップロード中…' : 'ファイルを選択'}</span>
                  </Button>
                  {form.photo_url && (
                    <span className="flex items-center gap-1 text-xs text-zinc-600 bg-zinc-100 rounded-full px-2.5 py-1" data-testid="bihin-form-file-chip">
                      <span className="truncate max-w-40">{form.photo_filename || '添付済み'}</span>
                      <button type="button"
                        onClick={() => setForm((p) => ({ ...p, photo_url: '', photo_filename: '', photo_is_pdf: false }))}
                        className="text-zinc-400 hover:text-rose-500" data-testid="bihin-form-file-remove">
                        <X size={12} />
                      </button>
                    </span>
                  )}
                </div>
              </FormSection>
            </>
          )}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bihin-form-cancel">キャンセル</Button>
          <Button onClick={save} disabled={busy} data-testid="bihin-form-save">
            {busy ? <ArrowsClockwise className="animate-spin" size={14} /> : null}
            <span className={busy ? 'ml-1' : ''}>{item ? '更新する' : '登録する'}</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
